import React, { useState, useEffect } from 'react';
import {
  Table, Button, Input, Select, Space, Modal, Form, Tag, Popconfirm,
  message, Card, Typography, Row, Col, Badge, Divider, Alert, Checkbox
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  DisconnectOutlined, UserOutlined, RobotOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useActionColumn } from '../utils/tableLayout';

const { Title } = Typography;
const { Option } = Select;

function UserRobotBindings() {
  const actionColumn = useActionColumn(220);
  const [bindings, setBindings] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableReceivers, setAvailableReceivers] = useState([]);
  const [availableSenders, setAvailableSenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBinding, setEditingBinding] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'active'
  });

  const [form] = Form.useForm();
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadBindings();
    loadUsers();
    loadAvailableRobots();
  }, [pagination.current, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBindings = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };

      const response = await api.get('/user-robot-bindings/all', { params });
      setBindings(response.data.bindings);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      console.error('加载用户机器人绑定失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('加载用户列表失败:', error);
    }
  };

  const loadAvailableRobots = async () => {
    try {
      const [receiversRes, sendersRes] = await Promise.all([
        api.get('/user-robot-bindings/available-receivers'),
        api.get('/user-robot-bindings/available-senders')
      ]);
      
      setAvailableReceivers(receiversRes.data.receivers || []);
      setAvailableSenders(sendersRes.data.senders || []);
    } catch (error) {
      console.error('加载可用机器人失败:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingBinding(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingBinding(record);
    form.setFieldsValue({
      userId: record.user?._id,
      receiverRobotId: record.receiverRobot?.robot?._id,
      senderRobotId: record.senderRobot?.robot?._id,
      'permissions.canRead': record.permissions?.canRead,
      'permissions.canWrite': record.permissions?.canWrite,
      'permissions.canDelete': record.permissions?.canDelete,
      'permissions.canManageSettings': record.permissions?.canManageSettings,
      notes: record.notes
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        userId: values.userId,
        receiverRobotId: values.receiverRobotId,
        senderRobotId: values.senderRobotId,
        permissions: {
          canRead: values['permissions.canRead'] || false,
          canWrite: values['permissions.canWrite'] || false,
          canDelete: values['permissions.canDelete'] || false,
          canManageSettings: values['permissions.canManageSettings'] || false
        },
        notes: values.notes
      };

      await api.post('/user-robot-bindings/bind', submitData);
      message.success(editingBinding ? '绑定更新成功' : '绑定创建成功');
      setModalVisible(false);
      loadBindings();
    } catch (error) {
      console.error('保存绑定失败:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('保存失败，请稍后重试');
      }
    }
  };

  const handleUnbindReceiver = async (userId) => {
    try {
      await api.post('/user-robot-bindings/unbind-receiver', { userId });
      message.success('接收机器人解绑成功');
      loadBindings();
    } catch (error) {
      console.error('解绑接收机器人失败:', error);
      message.error('解绑失败');
    }
  };

  const handleUnbindSender = async (userId) => {
    try {
      await api.post('/user-robot-bindings/unbind-sender', { userId });
      message.success('发送机器人解绑成功');
      loadBindings();
    } catch (error) {
      console.error('解绑发送机器人失败:', error);
      message.error('解绑失败');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/user-robot-bindings/${userId}`);
      message.success('绑定删除成功');
      loadBindings();
    } catch (error) {
      console.error('删除绑定失败:', error);
      message.error('删除失败');
    }
  };

  const getRobotTypeTag = (functionType) => {
    const typeMap = {
      receiver: { color: 'blue', text: '接收' },
      sender: { color: 'green', text: '发送' },
      both: { color: 'purple', text: '收发' }
    };
    const config = typeMap[functionType] || { color: 'gray', text: '未知' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '用户',
      dataIndex: 'user',
      key: 'user',
      width: 150,
      render: (user) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{user?.username}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{user?.email}</div>
          </div>
        </Space>
      )
    },
    {
      title: '接收机器人 (R1)',
      key: 'receiverRobot',
      width: 200,
      render: (_, record) => {
        const receiver = record.receiverRobot;
        if (!receiver?.robot) {
          return <Badge status="default" text="未绑定" />;
        }
        return (
          <div>
            <Space>
              <RobotOutlined style={{ color: '#1890ff' }} />
              <div>
                <div>{receiver.robot.name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {receiver.worktoolRobotId?.substring(0, 10)}...
                </div>
              </div>
              {getRobotTypeTag(receiver.robot.functionType)}
            </Space>
          </div>
        );
      }
    },
    {
      title: '发送机器人 (R2)',
      key: 'senderRobot',
      width: 200,
      render: (_, record) => {
        const sender = record.senderRobot;
        if (!sender?.robot) {
          return <Badge status="default" text="未绑定" />;
        }
        return (
          <div>
            <Space>
              <RobotOutlined style={{ color: '#52c41a' }} />
              <div>
                <div>{sender.robot.name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {sender.worktoolRobotId?.substring(0, 10)}...
                </div>
              </div>
              {getRobotTypeTag(sender.robot.functionType)}
            </Space>
          </div>
        );
      }
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 150,
      render: (permissions) => (
        <div>
          {permissions?.canRead && <Tag color="blue" size="small">读取</Tag>}
          {permissions?.canWrite && <Tag color="green" size="small">写入</Tag>}
          {permissions?.canDelete && <Tag color="red" size="small">删除</Tag>}
          {permissions?.canManageSettings && <Tag color="purple" size="small">管理</Tag>}
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Badge
          status={status === 'active' ? 'success' : 'default'}
          text={status === 'active' ? '活跃' : '非活跃'}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      ...actionColumn,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          {record.receiverRobot?.robot && (
            <Popconfirm
              title="确定解绑接收机器人吗？"
              onConfirm={() => handleUnbindReceiver(record.user._id)}
            >
              <Button 
                type="link" 
                icon={<DisconnectOutlined />} 
                size="small"
                style={{ color: '#ff7875' }}
              >
                解绑R1
              </Button>
            </Popconfirm>
          )}
          {record.senderRobot?.robot && (
            <Popconfirm
              title="确定解绑发送机器人吗？"
              onConfirm={() => handleUnbindSender(record.user._id)}
            >
              <Button 
                type="link" 
                icon={<DisconnectOutlined />} 
                size="small"
                style={{ color: '#ff7875' }}
              >
                解绑R2
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确定删除这个绑定吗？"
            onConfirm={() => handleDelete(record.user._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>用户机器人绑定管理</Title>
        <div className="description">管理用户与接收/发送机器人的绑定关系</div>
      </div>

      <Alert
        message="机器人分类说明"
        description={
          <div>
            <p><strong>R1 (接收机器人)</strong>: 用于webhook接收微信消息，处理用户发送的消息</p>
            <p><strong>R2 (发送机器人)</strong>: 用于向微信群发送消息；全局推送反馈使用独立配置</p>
            <p>每个用户只能绑定1个接收机器人和1个发送机器人，可以是同一个机器人</p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card>
        <div className="table-toolbar">
          <div className="left">
            <Input.Search
              placeholder="搜索用户名、邮箱、机器人名称"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <Select
              placeholder="状态"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => handleFilterChange('status', value)}
              value={filters.status}
            >
              <Option value="active">活跃</Option>
              <Option value="inactive">非活跃</Option>
              <Option value="pending">待处理</Option>
            </Select>
          </div>
          <div className="right">
            {hasPermission('userManagement', 'write') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                添加绑定
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={bindings}
          rowKey={(record) => record.user?._id}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          tableLayout="fixed"
        />
      </Card>

      {/* 添加/编辑绑定模态框 */}
      <Modal
        title={editingBinding ? '编辑机器人绑定' : '添加机器人绑定'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="userId"
            label="用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              placeholder="请选择用户"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {users.map(user => (
                <Option key={user._id} value={user._id}>
                  {user.username} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider>机器人配置</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="receiverRobotId"
                label="接收机器人 (R1)"
                extra="用于webhook接收微信消息"
              >
                <Select placeholder="请选择接收机器人">
                  {availableReceivers.map(robot => (
                    <Option key={robot._id} value={robot._id}>
                      <Space>
                        {robot.name}
                        {getRobotTypeTag(robot.functionType)}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="senderRobotId"
                label="发送机器人 (R2)"
                extra="用于向微信群发送消息"
              >
                <Select placeholder="请选择发送机器人">
                  {availableSenders.map(robot => (
                    <Option key={robot._id} value={robot._id}>
                      <Space>
                        {robot.name}
                        {getRobotTypeTag(robot.functionType)}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>权限设置</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="permissions.canRead" valuePropName="checked">
                <Checkbox>读取权限</Checkbox>
              </Form.Item>
              <Form.Item name="permissions.canWrite" valuePropName="checked">
                <Checkbox>写入权限</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="permissions.canDelete" valuePropName="checked">
                <Checkbox>删除权限</Checkbox>
              </Form.Item>
              <Form.Item name="permissions.canManageSettings" valuePropName="checked">
                <Checkbox>管理设置权限</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingBinding ? '更新绑定' : '创建绑定'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UserRobotBindings;
